import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  IconButton,
  Box,
  Chip,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PinDropOutlinedIcon from "@mui/icons-material/PinDropOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import LocationOffOutlinedIcon from "@mui/icons-material/LocationOffOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ViewLocation from "@/components/ViewLocation/ViewLocation";
import { getUserGeofenceAreaById } from "@/store/slices/GeofenceManagementSlice";
import toast from "@/lib/toast";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "@/store/store";

interface IUserGeofences {
  UserGeofencesModalOpen: boolean;
  UserGeofencesCloseModal: () => void;
  selectedUser: any;
  geoFencingArea: any[];
}

const getTypeStyle = (type?: string) => {
  const t = (type || "").toLowerCase();
  if (t.includes("office")) {
    return {
      bg: "#f0f4ff",
      color: "#5d78ff",
      border: "#e0e7ff",
    };
  }
  if (t.includes("home")) {
    return {
      bg: "#ecfdf5",
      color: "#10b981",
      border: "#d1fae5",
    };
  }
  if (t.includes("client")) {
    return {
      bg: "#fffbeb",
      color: "#d97706",
      border: "#fef3c7",
    };
  }
  return {
    bg: "#f8fafc",
    color: "#64748b",
    border: "#e2e8f0",
  };
};

const UserGeofences: React.FC<IUserGeofences> = ({
  UserGeofencesModalOpen,
  UserGeofencesCloseModal,
  selectedUser,
  geoFencingArea,
}) => {
  const [location, setLocation] = useState({ lat: "", lng: "" });
  const [selectedGeofenceTitle, setSelectedGeofenceTitle] = useState<string>("");
  const [modalOpenViewLocation, setModalOpenViewLocation] = useState(false);
  const [loadingId, setLoadingId] = useState<number | null>(null);

  const dispatch = useDispatch<AppDispatch>();

  const inGeofences = useMemo(
    () => selectedUser?.PunchInGeoFences || [],
    [selectedUser]
  );
  const outGeofences = useMemo(
    () => selectedUser?.PunchOutGeoFences || [],
    [selectedUser]
  );
  const totalCount = inGeofences.length + outGeofences.length;

  const handleOnClickLocation = async (geofence: any) => {
    try {
      setSelectedGeofenceTitle(
        `${geofence.Name} ${geofence?.GeoFenceTypeName ? `(${geofence?.GeoFenceTypeName})` : ""}`
      );

      // Coordinates already available on the object
      if (geofence?.Latitude != null && geofence?.Longitude != null) {
        setLocation({
          lat: String(geofence.Latitude),
          lng: String(geofence.Longitude),
        });
        setModalOpenViewLocation(true);
        return;
      }

      // Check local area list
      const localArea = geoFencingArea?.find((g: any) => g.Id === geofence.Id);
      if (localArea?.Latitude != null && localArea?.Longitude != null) {
        setLocation({
          lat: String(localArea.Latitude),
          lng: String(localArea.Longitude),
        });
        setModalOpenViewLocation(true);
        return;
      }

      // Fetch from API
      setLoadingId(geofence.Id);
      const area: any = await dispatch(
        getUserGeofenceAreaById(geofence.Id)
      ).unwrap();

      if (area?.Latitude != null && area?.Longitude != null) {
        setLocation({
          lat: String(area.Latitude),
          lng: String(area.Longitude),
        });
        setModalOpenViewLocation(true);
        return;
      }

      toast.error("Coordinates not available for this geofence");
    } catch (error: any) {
      toast.error(error || "Failed to load geofence location");
    } finally {
      setLoadingId(null);
    }
  };

  const renderGeofenceList = (items: any[], typeLabel: string) => {
    if (items.length === 0) {
      return (
        <Box
          sx={{
            py: 2,
            px: 2,
            backgroundColor: "#fbfcfe",
            borderRadius: "8px",
            border: "1px dashed #e2e8f0",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            color: "#94a3b8",
          }}
        >
          <LocationOffOutlinedIcon sx={{ fontSize: 20, color: "#cbd5e1" }} />
          <Typography
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "12px",
              fontWeight: 400,
              color: "#94a3b8",
            }}
          >
            No {typeLabel.toLowerCase()} geofences assigned
          </Typography>
        </Box>
      );
    }

    return (
      <div className="flex flex-col gap-1.5">
        {items.map((geofence: any, index: number) => {
          const typeStyle = getTypeStyle(geofence?.GeoFenceTypeName);
          const isItemLoading = loadingId === geofence.Id;

          return (
            <Box
              key={`${geofence.Id || index}-${typeLabel}`}
              onClick={() => handleOnClickLocation(geofence)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                p: "8px 12px",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                border: "1px solid #edf0f7",
                cursor: "pointer",
                transition: "all 0.15s ease",
                "&:hover": {
                  borderColor: "#cbd5e1",
                  backgroundColor: "#fbfcfe",
                  "& .geofence-action-btn": {
                    color: "#5d78ff",
                    backgroundColor: "#eef2ff",
                  },
                },
              }}
            >
              {/* Left Side: Icon + Name */}
              <div className="flex items-center gap-2.5 min-w-0">
                <LocationOnOutlinedIcon
                  sx={{
                    fontSize: 16,
                    color: "#5d78ff",
                    flexShrink: 0,
                  }}
                />

                <div className="min-w-0">
                  <Typography
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontWeight: 400,
                      fontSize: "12.5px",
                      color: "#1e2433",
                      lineHeight: 1.3,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {geofence.Name}
                  </Typography>
                </div>
              </div>

              {/* Right Side: Type Badge + Action */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {geofence?.GeoFenceTypeName && (
                  <Chip
                    label={geofence.GeoFenceTypeName}
                    size="small"
                    sx={{
                      height: "20px",
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "10.5px",
                      fontWeight: 400,
                      backgroundColor: typeStyle.bg,
                      color: typeStyle.color,
                      border: `1px solid ${typeStyle.border}`,
                      borderRadius: "4px",
                      px: 0.5,
                    }}
                  />
                )}

                <Tooltip title="View on Map">
                  <div
                    className="geofence-action-btn w-6 h-6 rounded flex items-center justify-center transition-all"
                    style={{
                      color: "#94a3b8",
                    }}
                  >
                    {isItemLoading ? (
                      <span className="inline-block w-3 h-3 border-2 border-[#5d78ff] border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <MapOutlinedIcon sx={{ fontSize: 14 }} />
                    )}
                  </div>
                </Tooltip>
              </div>
            </Box>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Dialog
        open={UserGeofencesModalOpen}
        onClose={UserGeofencesCloseModal}
        fullWidth
        maxWidth={false}
        slotProps={{
          paper: {
            sx: {
              width: "450px",
              maxWidth: "94vw",
              borderRadius: "14px",
              overflow: "hidden",
              border: "1px solid #e9edf5",
              boxShadow: "0 10px 30px rgba(38, 46, 77, 0.12)",
            },
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            px: 2.5,
            py: 1.75,
            borderBottom: "1px solid #edf0f7",
            background: "linear-gradient(180deg, #fafbfe 0%, #ffffff 100%)",
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-[#eef2ff] text-[#5d78ff] flex items-center justify-center">
                <PinDropOutlinedIcon sx={{ fontSize: 17 }} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Typography
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontWeight: 600,
                      fontSize: "14.5px",
                      color: "#1e2433",
                      lineHeight: 1.25,
                    }}
                  >
                    User Geofences
                  </Typography>
                  <span className="text-[11px] font-medium text-[#5d78ff] bg-[#eef2ff] px-2 py-0.2 rounded-full">
                    {totalCount} {totalCount === 1 ? "area" : "areas"}
                  </span>
                </div>
                <Typography
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "12px",
                    color: "#64748b",
                    mt: 0.2,
                  }}
                >
                  {selectedUser?.UserName || "Selected User"}
                </Typography>
              </div>
            </div>

            <IconButton
              size="small"
              onClick={UserGeofencesCloseModal}
              sx={{
                color: "#94a3b8",
                "&:hover": { color: "#1e2433", backgroundColor: "#f1f5f9" },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </div>
        </DialogTitle>

        {/* Content */}
        <DialogContent
          sx={{
            px: 2.5,
            py: 2,
            backgroundColor: "#ffffff",
            maxHeight: "60vh",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Punch In Section */}
          <Box>
            <div className="flex items-center justify-between mb-1.5 mt-3">
              <Typography
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "11.5px",
                  fontWeight: 500,
                  color: "#64748b",
                }}
              >
                Punch In Geofences
              </Typography>
              <span className="text-[11px] font-[Poppins] text-[#64748b] bg-[#f1f5f9] px-1.5 py-0.2 rounded">
                {inGeofences.length}
              </span>
            </div>
            {renderGeofenceList(inGeofences, "Punch In")}
          </Box>

          {/* Punch Out Section */}
          <Box>
            <div className="flex items-center justify-between mb-1.5">
              <Typography
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "11.5px",
                  fontWeight: 500,
                  color: "#64748b",
                }}
              >
                Punch Out Geofences
              </Typography>
              <span className="text-[11px] font-[Poppins] text-[#64748b] bg-[#f1f5f9] px-1.5 py-0.2 rounded">
                {outGeofences.length}
              </span>
            </div>
            {renderGeofenceList(outGeofences, "Punch Out")}
          </Box>
        </DialogContent>

        {/* Footer */}
        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: "1px solid #edf0f7",
            backgroundColor: "#fafbfe",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className="flex items-center gap-1.5 text-[#94a3b8]">
         
          </div>

          <Button
            onClick={UserGeofencesCloseModal}
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "13px",
              fontWeight: 500,
              textTransform: "none",
              backgroundColor: "#5d78ff",
              color: "#ffffff",
              borderRadius: "8px",
              px: 2.5,
              height: "34px",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#4863e6",
                boxShadow: "none",
              },
            }}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submodal for Map View */}
      {modalOpenViewLocation && (
        <ViewLocation
          title={selectedGeofenceTitle || "Geofence Location"}
          width="740px"
          modalOpen={modalOpenViewLocation}
          closeModal={() => {
            setModalOpenViewLocation(false);
            setSelectedGeofenceTitle("");
          }}
          lat={location.lat}
          lng={location.lng}
        />
      )}
    </>
  );
};

export default UserGeofences;


